import {
  createTicketCategoryRequestSchema,
  createTicketTypeDefinitionRequestSchema,
  okResponseSchema,
  ticketCatalogAdminResponseSchema,
  ticketCategoryAdminSchema,
  ticketTypeDefinitionSchema,
  updateTicketCatalogSettingsRequestSchema,
  updateTicketCategoryRequestSchema,
  updateTicketTypeDefinitionRequestSchema,
  type CreateTicketCategoryRequest,
  type CreateTicketTypeDefinitionRequest,
  type UpdateTicketCatalogSettingsRequest,
  type UpdateTicketCategoryRequest,
  type UpdateTicketTypeDefinitionRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ticketLookupsQueryKey } from '@/products/helpdesk/features/tickets/use-tickets';
import { apiFetch } from '@/lib/api';

export const ticketCatalogAdminQueryKey = ['settings', 'ticket-catalog'] as const;

async function invalidateCatalog(queryClient: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ticketCatalogAdminQueryKey }),
    queryClient.invalidateQueries({ queryKey: ticketLookupsQueryKey }),
  ]);
}

export function useTicketCatalogAdmin() {
  return useQuery({
    queryKey: ticketCatalogAdminQueryKey,
    queryFn: () => apiFetch('/settings/ticket-catalog', ticketCatalogAdminResponseSchema),
  });
}

export function usePatchTicketCatalogSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateTicketCatalogSettingsRequest) =>
      apiFetch('/settings/ticket-catalog', ticketCatalogAdminResponseSchema, {
        method: 'PATCH',
        body: JSON.stringify(updateTicketCatalogSettingsRequestSchema.parse(body)),
      }),
    onSuccess: async (data) => {
      queryClient.setQueryData(ticketCatalogAdminQueryKey, data);
      await invalidateCatalog(queryClient);
    },
  });
}

export function useCreateTicketCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTicketCategoryRequest) =>
      apiFetch('/settings/ticket-categories', ticketCategoryAdminSchema, {
        method: 'POST',
        body: JSON.stringify(createTicketCategoryRequestSchema.parse(body)),
      }),
    onSuccess: async () => {
      await invalidateCatalog(queryClient);
    },
  });
}

export function useUpdateTicketCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateTicketCategoryRequest }) =>
      apiFetch(`/settings/ticket-categories/${id}`, ticketCategoryAdminSchema, {
        method: 'PATCH',
        body: JSON.stringify(updateTicketCategoryRequestSchema.parse(body)),
      }),
    onSuccess: async () => {
      await invalidateCatalog(queryClient);
    },
  });
}

export function useDeleteTicketCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/settings/ticket-categories/${id}`, okResponseSchema, { method: 'DELETE' }),
    onSuccess: async () => {
      await invalidateCatalog(queryClient);
    },
  });
}

export function useCreateTicketTypeDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTicketTypeDefinitionRequest) =>
      apiFetch('/settings/ticket-type-definitions', ticketTypeDefinitionSchema, {
        method: 'POST',
        body: JSON.stringify(createTicketTypeDefinitionRequestSchema.parse(body)),
      }),
    onSuccess: async () => {
      await invalidateCatalog(queryClient);
    },
  });
}

export function useUpdateTicketTypeDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateTicketTypeDefinitionRequest }) =>
      apiFetch(`/settings/ticket-type-definitions/${id}`, ticketTypeDefinitionSchema, {
        method: 'PATCH',
        body: JSON.stringify(updateTicketTypeDefinitionRequestSchema.parse(body)),
      }),
    onSuccess: async () => {
      await invalidateCatalog(queryClient);
    },
  });
}

export function useDeleteTicketTypeDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/settings/ticket-type-definitions/${id}`, okResponseSchema, { method: 'DELETE' }),
    onSuccess: async () => {
      await invalidateCatalog(queryClient);
    },
  });
}
